import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { isWeb } from '../storage';

// V1 interfaces (for mobile)
interface AlertChannelV1 {
  SMS: string;
  EMAIL: string;
}

interface AlertDetailV1 {
  name: string;
  selectedValue: string;
}

interface AlertV1 {
  alert: string;
  channels: AlertChannelV1;
  alertDetails: AlertDetailV1[];
  activeFlag: boolean;
}

// V2 interfaces (for web)
interface AlertChannelV2 {
  SMS?: string;
  EMAIL?: string;
}

interface AlertDetailV2 {
  name: string;
  selectedValue: string | string[];
  availableValues?: string[];
}

interface AlertV2 {
  alert: string;
  channels: AlertChannelV2;
  alertDetails: AlertDetailV2[];
  activeFlag: string;
}

interface AlertCategoryV2 {
  categoryName: string;
  details: AlertV2[];
  activeAlertCount: number;
}

// Common interfaces
interface ContactPhoneNumber {
  type: string;
  value: string;
}

interface ContactAddress {
  country: {
    code: string;
    description: string;
  };
  city: string;
  postalCode: string;
  line1: string;
  line2?: string;
  areaLevelOne: {
    description: string;
    code: string;
  };
}

interface AvailableCard {
  customerType: string;
  nameOnCard: string;
  customerId: string;
  cardNumber: string;
}

interface ContactPreferences {
  emailAddress: string;
  phoneNumber: ContactPhoneNumber[];
  address: ContactAddress;
  correspondenceLanguage: {
    code: string;
    description: string;
  };
  availableCards: AvailableCard[];
  statementPreference?: string;
}

// New interfaces for alert contact preferences
interface CustomerIdentifiers {
  accountId: string;
  enrolledCustomerId: string;
  unitName: string;
  enrolledUniqueId: string;
  customerId: string;
  cardNumber: string;
  contactMasterId: string;
  enrolledCardNumber: string;
  externalUserName: string;
  uniqueId: string;
}

interface Phone {
  type: string;
  value: string;
  number: string;
}

interface SelectedPhone {
  type: string;
  verificationStatus: string;
  value: string;
}

interface Program {
  phones: Phone[];
  programName: string;
  selectedPhones: SelectedPhone[];
}

interface Message {
  status: string;
  detail: string;
  title: string;
  errorCode: string;
  errors: string[];
}

interface PhoneOptionsResponse {
  assignmentId: string;
  id: number;
  statusCode: string;
}

interface PhoneUpdateResponse {
  confirmationNumber: string;
  status: string;
  statusCode: string;
}

interface EmailOptionsResponse {
  assignmentId: string;
  id: number;
  statusCode: string;
}

interface EmailUpdateResponse {
  confirmationNumber: string;
  status: string;
  statusCode: string;
}

interface AlertContactPreferencesResponse {
  customerIdentifiers: CustomerIdentifiers;
  emailAddress: string;
  programs: Program[];
  message: Message;
}

// Platform-specific response types
interface ManageAlertsResponseV1 {
  alerts: AlertV1[];
  contactPrefs: ContactPreferences;
}

interface ManageAlertsResponseV2 {
  alerts: AlertCategoryV2[];
  contactPrefs: ContactPreferences | null;
}

// Both platforms now use V2 alerts structure
type AlertsUnion = AlertCategoryV2[];

export interface ManageAlertsState {
  loading: boolean;
  alertsLoading: boolean;
  contactPrefsLoading: boolean;
  alerts: AlertsUnion;
  contactPrefs: ContactPreferences | null;
  alertContactPrefs: AlertContactPreferencesResponse | null;
  error: string | null;
  updateLoading: boolean;
  phoneOptionsLoading: boolean;
  phoneOptions: PhoneOptionsResponse | null;
  phoneUpdateLoading: boolean;
  emailOptionsLoading: boolean;
  emailOptions: EmailOptionsResponse | null;
  emailUpdateLoading: boolean;
  contactPrefsUpdateLoading: boolean;
}

const initialState: ManageAlertsState = {
  loading: false,
  alertsLoading: false,
  contactPrefsLoading: false,
  alerts: [],
  contactPrefs: null,
  alertContactPrefs: null,
  error: null,
  updateLoading: false,
  phoneOptionsLoading: false,
  phoneOptions: null,
  phoneUpdateLoading: false,
  emailOptionsLoading: false,
  emailOptions: null,
  emailUpdateLoading: false,
  contactPrefsUpdateLoading: false,
};

const manageAlertsSlice = createSlice({
  name: 'manageAlerts',
  initialState,
  reducers: {
    // Fetch active alerts
    fetchActiveAlertsRequest(
      state,
      action: PayloadAction<{ accountId: string; customerId: string }>
    ) {
      state.loading = true;
      state.alertsLoading = true;
      state.error = null;
    },
    fetchActiveAlertsSuccess(
      state,
      action: PayloadAction<ManageAlertsResponseV1 | ManageAlertsResponseV2>
    ) {
      state.loading = false;
      state.alertsLoading = false;
      
      // Both web and mobile now use V2 API response
      const responseV2 = action.payload as ManageAlertsResponseV2;
      state.alerts = responseV2.alerts || [];
      state.contactPrefs = responseV2.contactPrefs;
      
      state.error = null;
    },
    fetchActiveAlertsFailure(state, action: PayloadAction<string>) {
      state.loading = false;
      state.alertsLoading = false;
      state.error = action.payload;
    },
    // Fetch alert contact preferences
    fetchAlertContactPreferencesRequest(
      state,
      action: PayloadAction<{ accountId: string; customerId: string }>
    ) {
      state.loading = true;
      state.contactPrefsLoading = true;
      state.error = null;
    },
    fetchAlertContactPreferencesSuccess(
      state,
      action: PayloadAction<AlertContactPreferencesResponse>
    ) {
      state.loading = false;
      state.contactPrefsLoading = false;
      state.alertContactPrefs = action.payload;
      state.error = null;
    },
    fetchAlertContactPreferencesFailure(state, action: PayloadAction<string>) {
      state.loading = false;
      state.contactPrefsLoading = false;
      state.error = action.payload;
    },
    // Update alert preferences
    updateAlertPreferencesRequest(
      state,
      action: PayloadAction<{
        accountId: string;
        customerId: string;
        userNotificationPreferences: any[];
        recaptchaToken?:string|null|undefined;
      }>
    ) {
      state.loading = true;
      state.updateLoading = true;
      state.error = null;
    },
    updateAlertPreferencesSuccess(state, action: PayloadAction<any>) {
      state.loading = false;
      state.updateLoading = false;
      state.error = null;
    },
    updateAlertPreferencesFailure(state, action: PayloadAction<string>) {
      state.loading = false;
      state.updateLoading = false;
      state.error = action.payload;
    },
    // Fetch phone options
    fetchPhoneOptionsRequest(
      state,
      action: PayloadAction<{ accountId: string; customerId: string }>
    ) {
      state.phoneOptionsLoading = true;
      state.error = null;
    },
    fetchPhoneOptionsSuccess(
      state,
      action: PayloadAction<PhoneOptionsResponse>
    ) {
      state.phoneOptionsLoading = false;
      state.phoneOptions = action.payload;
      state.error = null;
    },
    fetchPhoneOptionsFailure(state, action: PayloadAction<string>) {
      state.phoneOptionsLoading = false;
      state.error = action.payload;
    },
    // Update phone for alerts
    updatePhoneForAlertsRequest(
      state,
      action: PayloadAction<{
        accountId: string;
        customerId: string;
        requestBody: any;
      }>
    ) {
      state.phoneUpdateLoading = true;
      state.error = null;
    },
    updatePhoneForAlertsSuccess(
      state,
      action: PayloadAction<PhoneUpdateResponse>
    ) {
      state.phoneUpdateLoading = false;
      state.error = null;
    },
    updatePhoneForAlertsFailure(state, action: PayloadAction<string>) {
      state.phoneUpdateLoading = false;
      state.error = action.payload;
    },
    // Fetch email options
    fetchEmailOptionsRequest(
      state,
      action: PayloadAction<{ accountId: string; customerId: string }>
    ) {
      state.emailOptionsLoading = true;
      state.error = null;
    },
    fetchEmailOptionsSuccess(
      state,
      action: PayloadAction<EmailOptionsResponse>
    ) {
      state.emailOptionsLoading = false;
      state.emailOptions = action.payload;
      state.error = null;
    },
    fetchEmailOptionsFailure(state, action: PayloadAction<string>) {
      state.emailOptionsLoading = false;
      state.error = action.payload;
    },
    // Update email for alerts
    updateEmailForAlertsRequest(
      state,
      action: PayloadAction<{
        accountId: string;
        customerId: string;
        requestBody: any;
      }>
    ) {
      state.emailUpdateLoading = true;
      state.error = null;
    },
    updateEmailForAlertsSuccess(
      state,
      action: PayloadAction<EmailUpdateResponse>
    ) {
      state.emailUpdateLoading = false;
      state.error = null;
    },
    updateEmailForAlertsFailure(state, action: PayloadAction<string>) {
      state.emailUpdateLoading = false;
      state.error = action.payload;
    },
    // Update alert contact preferences
    updateAlertContactPreferencesRequest(
      state,
      action: PayloadAction<{
        requestBody: any;
      }>
    ) {
      state.contactPrefsUpdateLoading = true;
      state.error = null;
    },
    updateAlertContactPreferencesSuccess(
      state,
      action: PayloadAction<AlertContactPreferencesResponse>
    ) {
      state.contactPrefsUpdateLoading = false;
      state.alertContactPrefs = action.payload;
      state.error = null;
    },
    updateAlertContactPreferencesFailure(state, action: PayloadAction<string>) {
      state.contactPrefsUpdateLoading = false;
      state.error = action.payload;
    },
    // Reset the slice
    resetManageAlertsSlice(state) {
      return initialState;
    },
    // Clear error state
    clearErrors(state) {
      state.error = null;
    },
  },
});

export const {
  fetchActiveAlertsRequest,
  fetchActiveAlertsSuccess,
  fetchActiveAlertsFailure,
  fetchAlertContactPreferencesRequest,
  fetchAlertContactPreferencesSuccess,
  fetchAlertContactPreferencesFailure,
  updateAlertPreferencesRequest,
  updateAlertPreferencesSuccess,
  updateAlertPreferencesFailure,
  fetchPhoneOptionsRequest,
  fetchPhoneOptionsSuccess,
  fetchPhoneOptionsFailure,
  updatePhoneForAlertsRequest,
  updatePhoneForAlertsSuccess,
  updatePhoneForAlertsFailure,
  fetchEmailOptionsRequest,
  fetchEmailOptionsSuccess,
  fetchEmailOptionsFailure,
  updateEmailForAlertsRequest,
  updateEmailForAlertsSuccess,
  updateEmailForAlertsFailure,
  updateAlertContactPreferencesRequest,
  updateAlertContactPreferencesSuccess,
  updateAlertContactPreferencesFailure,
  resetManageAlertsSlice,
  clearErrors,
} = manageAlertsSlice.actions;

export default manageAlertsSlice.reducer;
